import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ExternalLink } from '@/components/ui/external-link';
import { apiClient } from '../services/api';
import {
  hubspotDealUrl,
  hubspotContactUrl,
  stripeSubscriptionUrl,
  stripeCustomerUrl,
  stripePriceUrl,
  stripeMeterUrl,
  stripeInvoiceUrl,
} from '../utils/externalUrls';
import { apiErrorMessage } from '../utils/apiError';
import { formatCurrency } from '../utils/formatCurrency';
import { formatDate } from '../utils/formatStripe';
import { describeSyncStatus } from '../utils/syncStatus';
import {
  isSyncBlocked,
  syncBlockedApiReason,
  syncBlockedDetail,
} from '../utils/syncEligibility';

import { StripeIcon } from './icons/LogoIcons';
import InvoiceSummaryList from './invoices/InvoiceSummaryList';
import LinkExistingDialog from './LinkExistingDialog';
import SyncConfirmationDialog from './SyncConfirmationDialog';
import SyncFailureAlert from './SyncFailureAlert';
import WarningsPanel from './WarningsPanel';
import type { DealMapping, StripeInvoiceInfo } from '../types/api';




const DealDetail: React.FC = () => {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const [isSyncing, setIsSyncing] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showSyncConfirmation, setShowSyncConfirmation] = useState(false);
  // A sync that failed in THIS session. Distinct from deal.error_message,
  // which is the last persisted attempt's reason: a request that never
  // reached the server leaves no row to read it from.
  const [syncError, setSyncError] = useState<string | null>(null);
  // Extra invoice pages loaded on demand. The detail response carries the
  // first page; these are appended to it rather than replacing it, so the
  // list grows as the user pages back through history.
  const [extraInvoices, setExtraInvoices] = useState<StripeInvoiceInfo[]>([]);
  const [invoiceCursor, setInvoiceCursor] = useState<string | null>(null);
  const [invoicesExhausted, setInvoicesExhausted] = useState(false);

  // Paging state belongs to ONE deal. React Router reuses this component
  // across /sync_deals/:dealId routes, so without this a deal opened after
  // another would show the previous deal's loaded pages, and "Load more"
  // would send the previous deal's cursor to the new subscription.
  useEffect(() => {
    setExtraInvoices([]);
    setInvoiceCursor(null);
    setInvoicesExhausted(false);
  }, [dealId]);

  const { data: deal, isLoading, error, refetch } = useQuery({
    queryKey: ['deal', dealId],
    queryFn: () => apiClient.getDealDetail(dealId!),
    enabled: !!dealId,
  });

  const { data: tenantSettings } = useQuery({
    queryKey: ['tenantSettings'],
    queryFn: () => apiClient.getTenantSettings(),
    retry: 1,
  });

  const portalId = tenantSettings?.hubspot_portal_id;
  const stripeEnv = tenantSettings?.stripe_environment;
  const stripeAcctId = tenantSettings?.stripe_account_id;

  const handleSyncClick = () => {
    setShowSyncConfirmation(true);
  };

  const handleSync = async () => {
    if (!dealId) return;

    // Close the confirmation dialog immediately on confirm; the sync progress is
    // shown on the "Sync Now" button (isSyncing). Mirrors HubSpotDealDetail and
    // LinkExistingDialog, whose confirm handlers own their own close.
    setShowSyncConfirmation(false);
    setSyncError(null);
    setIsSyncing(true);
    try {
      await apiClient.syncDeal(dealId);
      // Refetch to get updated data. The refreshed status badge and, on
      // failure, SyncFailureAlert report the outcome in place — a blocking
      // alert() would have to be dismissed before either could be read.
      await refetch();
    } catch (err) {
      console.error('Sync failed:', err);
      // The server's reason first: Axios's own message is only ever
      // "Request failed with status code 500". Matches the table view, so
      // both sync surfaces report the same text for the same failure.
      setSyncError(apiErrorMessage(err, 'Failed to sync deal. Please try again.'));
    } finally {
      setIsSyncing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !deal) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-6xl mx-auto">
          <Button variant="ghost" onClick={() => navigate('/')} className="mb-4">
            <ChevronLeft className="mr-1 h-4 w-4" />
            Back
          </Button>
          <Alert variant="destructive">
            <AlertDescription>
              Failed to load deal details.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const isSynced = deal.sync_status === 'synced' && deal.stripe_subscription_id;
  // A linked deal has a CONFIRMED subscription — the user explicitly selected a
  // candidate. It carries a real stripe_subscription_id and must be shown as
  // linked, never as a "possible match".
  const isLinked = deal.sync_status === 'linked' && deal.stripe_subscription_id;
  // A pure one-time deal is billed as a standalone Stripe invoice — no
  // subscription exists. one_off_invoice_status is authoritative and
  // independent of the deal row's own sync_status/stripe_subscription_id.
  const isInvoiceBilled = !!deal.one_off_invoice_id && (
    deal.one_off_invoice_status === 'synced' || deal.one_off_invoice_status === 'linked'
  );
  // Synced, linked, and invoice-billed all mean something real is attached:
  // show the rich Stripe view and hide the sync/link actions.
  const isAttached = isSynced || isLinked || isInvoiceBilled;
  // Candidate subscriptions discovered for an unconfirmed deal (awaiting_selection)
  // — a "possible link". The top candidate's live sub info comes back in
  // deal.stripe_subscription (the backend resolves it from the candidate list;
  // stripe_subscription_id stays null until the user confirms).
  const candidates = deal.potential_historic_mapping?.candidates ?? [];
  const hasCandidates = !isAttached && candidates.length > 0;
  const otherMatchCount = Math.max(0, candidates.length - 1);
  const status = describeSyncStatus(deal.sync_status, {
    // Subscription-only: isAttached also covers invoice-billed deals, and
    // passing that here would recolour a failed deal's badge to neutral while
    // the failure alert below it stays red.
    isSubscriptionAttached: !!(isSynced || isLinked),
    isInvoiceBilled,
  });

  const allInvoices = [...(deal.invoices ?? []), ...extraInvoices];
  // The server reports more history on the first page; after that the cursor
  // walk decides, so a page that comes back short ends it.
  const hasMoreInvoices = invoicesExhausted
    ? false
    : extraInvoices.length > 0 || Boolean(deal.has_more_invoices);

  const loadMoreInvoices = async () => {
    if (!dealId) return;
    const cursor = invoiceCursor ?? allInvoices[allInvoices.length - 1]?.id;
    const page = await apiClient.listDealInvoices(dealId, cursor);
    setExtraInvoices((prev) => [...prev, ...page.invoices]);
    setInvoiceCursor(page.next_cursor ?? null);
    if (!page.has_more) setInvoicesExhausted(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto p-6">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate('/')} className="mb-6">
          <ChevronLeft className="mr-1 h-4 w-4" />
          Back to Deals
        </Button>

        {/* Header */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-2xl">
              {deal.dealname || 'Unnamed Deal'}
            </CardTitle>
            <CardDescription className="flex items-center gap-4">
              <span>Deal ID: {portalId ? (
                <ExternalLink href={hubspotDealUrl(portalId, deal.deal_id)} className="text-sm">
                  {deal.deal_id}
                </ExternalLink>
              ) : deal.deal_id}</span>
              {/* The deal list dropped its Company column, so this is where
                  the company is named. Takes the first association, matching
                  how the list used to pick one. */}
              {deal.companies?.[0]?.name && (
                <span>Company: {deal.companies[0].name}</span>
              )}
              {deal.amount && <span>Amount: {formatCurrency(deal.amount, deal.deal_currency_code)}</span>}
              {/* Only rendered when HubSpot actually recorded the stage entry
                  (portals must opt into stage-calculated properties). The deal
                  list shows last-modified instead, which always exists. */}
              {deal.closed_won_at && (
                <span>Closed won: {formatDate(deal.closed_won_at)}</span>
              )}
              {/* The label is what the stage is called in HubSpot; the raw
                  id ("closedwon", or an opaque number on a custom pipeline)
                  is the fallback when pipelines couldn't be read. */}
              {deal.dealstage && (
                <Badge variant="secondary">
                  {deal.dealstage_label ?? deal.dealstage}
                </Badge>
              )}
              {/* Distinguish a historically-linked deal from one Finrite synced. */}
              {isLinked ? (
                <Badge className="bg-blue-100 text-blue-900 hover:bg-blue-100">Linked</Badge>
              ) : isSynced ? (
                <Badge className="bg-green-100 text-green-900 hover:bg-green-100">Synced</Badge>
              ) : status.isFailed ? (
                <Badge variant={status.variant}>{status.label}</Badge>
              ) : null}
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Why the last sync failed, kept directly under the header rather than
            at the foot of the page where it went unread. */}
        {status.isFailed && (
          <SyncFailureAlert
            errorMessage={deal.error_message}
            lastSynced={deal.last_synced}
            className="mb-6"
          />
        )}

        {/* A sync that failed before the server recorded anything (network
            error, timeout). Shown only when the deal row itself isn't already
            reporting a failure, so one attempt never renders two alerts. */}
        {syncError && !status.isFailed && (
          <SyncFailureAlert errorMessage={syncError} className="mb-6" />
        )}

        {/* Main Content - Two Columns */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          {/* Left - HubSpot */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle>HubSpot</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Contract — the signed document Finrite manages for this deal.
                  Shown only when the deal's property holds a usable http(s)
                  URL; the backend nulls anything else, so this is never an
                  unvalidated href. Sits above Contacts because it is the
                  thing a person opening this page most often came to find. */}
              {deal.signed_doc_url && (
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Contract</h3>
                  <div className="text-sm bg-muted p-3 rounded-md">
                    <ExternalLink href={deal.signed_doc_url} className="text-sm font-medium">
                      Signed document
                    </ExternalLink>
                  </div>
                </div>
              )}

              {/* Contacts */}
              <div>
                <h3 className="font-semibold text-foreground mb-2">Contacts ({deal.contacts.length})</h3>
                {deal.contacts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No contacts</p>
                ) : (
                  <div className="space-y-2">
                    {deal.contacts.map((contact) => (
                      <div key={contact.id} className="text-sm bg-muted p-3 rounded-md">
                        <div className="font-medium">
                          {portalId ? (
                            <ExternalLink href={hubspotContactUrl(portalId, contact.id)} className="text-sm font-medium">
                              {contact.firstname} {contact.lastname}
                            </ExternalLink>
                          ) : (
                            <>{contact.firstname} {contact.lastname}</>
                          )}
                        </div>
                        {contact.email && <div className="text-muted-foreground">{contact.email}</div>}
                        {contact.phone && <div className="text-muted-foreground">{contact.phone}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Separator />

              {/* Line Items */}
              <div>
                <h3 className="font-semibold text-foreground mb-2">Line Items ({deal.line_items.length})</h3>
                {deal.line_items.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No line items</p>
                ) : (
                  <div className="space-y-2">
                    {deal.line_items.map((item) => (
                      <div key={item.id} className="text-sm bg-muted p-3 rounded-md">
                        <div className="font-medium">{item.name || 'Unnamed Item'}</div>
                        <div className="text-muted-foreground">
                          {item.quantity && `Qty: ${item.quantity}`}
                          {item.price && <>{` × ${formatCurrency(item.price, item.currency)}`}</>}
                          {item.amount && <>{` = ${formatCurrency(item.amount, item.currency)}`}</>}
                        </div>
                        {item.tax_rate_display && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Tax: {item.tax_rate_display}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Right - Stripe */}
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  isAttached ? 'bg-white' : 'bg-muted'
                }`}>
                  <StripeIcon />
                </div>
                <CardTitle>Stripe</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {isAttached || hasCandidates ? (
                <div className="space-y-4">
                  {/* Possible-match banner (shown only for an unconfirmed candidate) */}
                  {hasCandidates && (
                    <div className="space-y-2">
                      <Badge variant="secondary" className="bg-amber-100 text-amber-900 hover:bg-amber-100">
                        Possible match — not yet linked
                      </Badge>
                      <p className="text-sm text-muted-foreground">
                        A Stripe subscription that may already belong to this deal was found.
                        Review the details below and confirm to link it.
                      </p>
                    </div>
                  )}
                  {/* Subscription Info */}
                  {deal.stripe_subscription && (
                    <div>
                      <h3 className="font-semibold text-foreground mb-2">
                        {deal.billing_model === 'metered'
                          ? 'Metered Billing'
                          : deal.billing_model === 'metered_subscription'
                            ? 'Metered + Recurring Subscription'
                            : 'Subscription'}
                      </h3>
                      <div className="text-sm bg-muted p-3 rounded-md space-y-1">
                        <div>
                          <span className="text-muted-foreground">ID:</span>{' '}
                          {stripeEnv ? (
                            <ExternalLink href={stripeSubscriptionUrl(stripeEnv, deal.stripe_subscription.id, stripeAcctId)} className="text-sm font-mono">
                              {deal.stripe_subscription.id}
                            </ExternalLink>
                          ) : (
                            <span className="font-mono">{deal.stripe_subscription.id}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">Status:</span>
                          <Badge variant={deal.stripe_subscription.status === 'active' ? 'default' : 'secondary'}>
                            {deal.stripe_subscription.status}
                          </Badge>
                          {deal.billing_model && deal.billing_model !== 'subscription' && (
                            <Badge variant="secondary">metered</Badge>
                          )}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Customer:</span>{' '}
                          {stripeEnv ? (
                            <ExternalLink href={stripeCustomerUrl(stripeEnv, deal.stripe_subscription.customer_id, stripeAcctId)} className="text-xs font-mono">
                              {deal.stripe_subscription.customer_id}
                            </ExternalLink>
                          ) : (
                            <span className="font-mono text-xs">{deal.stripe_subscription.customer_id}</span>
                          )}
                        </div>
                        {/* Meter IDs */}
                        {deal.stripe_meter_ids && deal.stripe_meter_ids.length > 0 && (
                          <div>
                            <span className="text-muted-foreground">
                              {deal.stripe_meter_ids.length === 1 ? 'Meter:' : 'Meters:'}
                            </span>{' '}
                            {deal.stripe_meter_ids.map((meterId, idx) => (
                              <span key={meterId}>
                                {idx > 0 && ', '}
                                {stripeEnv ? (
                                  <ExternalLink href={stripeMeterUrl(stripeEnv, meterId, stripeAcctId)} className="text-xs font-mono">
                                    {meterId}
                                  </ExternalLink>
                                ) : (
                                  <span className="font-mono text-xs">{meterId}</span>
                                )}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* One-off invoice: shown only when there's no subscription
                      to display (pure one-time deals have no stripe_subscription). */}
                  {!deal.stripe_subscription && isInvoiceBilled && deal.one_off_invoice_id && (
                    <div>
                      <h3 className="font-semibold text-foreground mb-2">One-off invoice</h3>
                      <div className="text-sm bg-muted p-3 rounded-md space-y-1">
                        <div>
                          <span className="text-muted-foreground">ID:</span>{' '}
                          {stripeEnv ? (
                            <ExternalLink href={stripeInvoiceUrl(stripeEnv, deal.one_off_invoice_id, stripeAcctId)} className="text-sm font-mono">
                              {deal.one_off_invoice_id}
                            </ExternalLink>
                          ) : (
                            <span className="font-mono">{deal.one_off_invoice_id}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">Status:</span>
                          <Badge variant="default">
                            {deal.one_off_invoice_status === 'linked' ? 'Linked (invoice)' : 'Synced (invoice)'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Customer Tax IDs */}
                  {deal.stripe_customer_tax_ids && deal.stripe_customer_tax_ids.length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <h3 className="font-semibold text-foreground mb-2">Tax IDs</h3>
                        <div className="space-y-2">
                          {deal.stripe_customer_tax_ids.map((taxId) => (
                            <div key={taxId.id} className="text-sm bg-muted p-3 rounded-md flex items-center gap-2">
                              <Badge variant="secondary">{taxId.type_display || taxId.type}</Badge>
                              <span className="font-mono text-xs">{taxId.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Products/Prices */}
                  {deal.stripe_products && deal.stripe_products.length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <h3 className="font-semibold text-foreground mb-2">Products & Prices</h3>
                        <div className="space-y-2">
                          {deal.stripe_products.map((product, idx) => (
                            <div key={idx} className="text-sm bg-muted p-3 rounded-md">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{product.product_name || 'Product'}</span>
                                {product.billing_type === 'metered' && (
                                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                    metered
                                  </Badge>
                                )}
                              </div>
                              <div className="text-muted-foreground">
                                {product.billing_type === 'metered'
                                  ? 'Usage-based pricing'
                                  : product.price_amount && product.price_currency
                                    ? `Price: ${(product.price_amount / 100).toFixed(2)} ${product.price_currency.toUpperCase()}`
                                    : 'Price: N/A'}
                              </div>
                              <div className="text-xs text-muted-foreground font-mono mt-1">
                                {stripeEnv ? (
                                  <ExternalLink href={stripePriceUrl(stripeEnv, product.price_id, stripeAcctId)} className="text-xs font-mono">
                                    {product.price_id}
                                  </ExternalLink>
                                ) : (
                                  product.price_id
                                )}
                              </div>
                              {product.stripe_meter_id && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  <span>Meter: </span>
                                  {stripeEnv ? (
                                    <ExternalLink href={stripeMeterUrl(stripeEnv, product.stripe_meter_id, stripeAcctId)} className="text-xs font-mono">
                                      {product.stripe_meter_id}
                                    </ExternalLink>
                                  ) : (
                                    <span className="font-mono">{product.stripe_meter_id}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Candidate footer: how it matched + other possible matches */}
                  {hasCandidates && (
                    <>
                      <Separator />
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">
                          Matched via {candidates[0].matched_via.replace('_', ' ')}
                        </p>
                        {otherMatchCount > 0 && (
                          <p className="text-xs text-muted-foreground">
                            +{otherMatchCount} other possible match{otherMatchCount > 1 ? 'es' : ''} — choose in the link dialog.
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">Not synced to Stripe</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Invoices — full width rather than inside the Stripe column, since a
            line-item breakdown needs the room. */}
        {allInvoices.length > 0 && (
          <Card className="mb-6">
            <CardHeader className="pb-4">
              <CardTitle>Invoices</CardTitle>
            </CardHeader>
            <CardContent>
              <InvoiceSummaryList
                invoices={allInvoices}
                hasMore={hasMoreInvoices}
                onLoadMore={loadMoreInvoices}
              />
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        {!isAttached && (
          <Card>
            <CardContent className="p-6">
              {/* The deals list includes deals whose stage cannot sync — open
                  ones carrying a quote — and links straight here, so this
                  page has to apply the same gate. Linking stays available:
                  attaching an existing subscription is not a sync. */}
              {isSyncBlocked(deal) && (
                <p
                  className="mb-4 text-sm text-muted-foreground"
                  title={syncBlockedApiReason(deal)}
                >
                  {syncBlockedDetail(deal)}
                </p>
              )}
              <div className="flex space-x-4">
                <Button
                  onClick={handleSyncClick}
                  disabled={isSyncing || isSyncBlocked(deal)}
                  className="flex-1 h-12"
                  size="lg"
                >
                  {isSyncing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    'Sync to Stripe'
                  )}
                </Button>
                <Button
                  onClick={() => setShowLinkDialog(true)}
                  disabled={isSyncing}
                  variant="outline"
                  className="flex-1 h-12"
                  size="lg"
                >
                  {hasCandidates ? 'Confirm link…' : 'Link Existing…'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Non-fatal warnings from the sync pipeline. A failed sync's error
            now sits under the header, so it stays dominant over these.
            Subtle amber palette signals "advisory, not failure". */}
        {deal.warnings && deal.warnings.length > 0 && (
          <WarningsPanel warnings={deal.warnings} />
        )}
      </div>

      <SyncConfirmationDialog
        open={showSyncConfirmation}
        dealId={dealId!}
        onOpenChange={setShowSyncConfirmation}
        onConfirm={handleSync}
        isSyncing={isSyncing}
      />

      {showLinkDialog && (
        <LinkExistingDialog
          deal={{
            hubspot_deal_id: deal.deal_id,
            dealname: deal.dealname,
            sync_status: deal.sync_status,
          } as DealMapping}
          open={showLinkDialog}
          onOpenChange={(o) => {
            setShowLinkDialog(o);
            if (!o) refetch();
          }}
        />
      )}
    </div>
  );
};

export default DealDetail;
